import { useForm } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/date-picker";
import Combobox from "@/components/combobox";
import { DraftInvoice } from "@/types";

type Props = {
  draftInvoice: DraftInvoice;
};

export function PublishInvoiceForm({ draftInvoice }: Props) {
  const buyerName = draftInvoice.data.buyer?.name;
  const isRubyLiftBuyer =
    buyerName?.includes("Ruby Lift") || buyerName?.includes("Руби Лифт");

  const merchantData = isRubyLiftBuyer
    ? draftInvoice.data.vendor
    : draftInvoice.data.buyer;

  const { data, setData, post, processing, errors, transform } = useForm({
    merchant_name: merchantData?.name ?? "",
    type: isRubyLiftBuyer ? "expense" : "revenue",
    number: draftInvoice.data.number ?? "",
    issue_date: draftInvoice.data.issue_date ?? "",
    tax_event_date: draftInvoice.data.tax_event_date ?? "",
    due_date: draftInvoice.data.due_date ?? "",
    base_amount: draftInvoice.data.base_amount ?? "",
    vat_amount: draftInvoice.data.vat_amount ?? "",
    total_amount: draftInvoice.data.total_amount ?? "",
    currency: draftInvoice.data.currency ?? "",
  });

  transform((data) => {
    return { draft_invoice: data };
  });

  const invoiceTypes = [
    {
      label: "Expense",
      value: "expense",
    },
    {
      label: "Revenue",
      value: "revenue",
    },
  ];

  function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();

    post(`/draft_invoices/${draftInvoice.id}/publish`, {
      preserveScroll: "errors",
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-3">
        <Label htmlFor="merchant_name">Merchant</Label>
        <Input
          id="merchant_name"
          value={data.merchant_name}
          onChange={(e) => setData("merchant_name", e.target.value)}
        />
        {errors.merchant_name && (
          <p className="text-destructive text-sm">{errors.merchant_name}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_type">Type</Label>
        <Combobox
          itemType="type"
          id="invoice_type"
          value={data.type}
          items={invoiceTypes}
          onSelect={(type) => {
            setData("type", type);
          }}
        />
        {errors.type && (
          <p className="text-destructive text-sm">{errors.type}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_number">Number</Label>
        <Input
          id="invoice_number"
          value={data.number}
          onChange={(e) => setData("number", e.target.value)}
        />
        {errors.number && (
          <p className="text-destructive text-sm">{errors.number}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_date">Issue date</Label>
        <DatePicker
          id="invoice_date"
          selected={data.issue_date}
          onSelect={(issueDate) => {
            if (issueDate) {
              setData("issue_date", format(issueDate, "yyyy-MM-dd"));
            } else {
              setData("issue_date", "");
            }
          }}
        />
        {errors.issue_date && (
          <p className="text-destructive text-sm">{errors.issue_date}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="tax_event_date">Tax event date</Label>
        <DatePicker
          id="tax_event_date"
          selected={data.tax_event_date}
          onSelect={(taxEventDate) => {
            if (taxEventDate) {
              setData("tax_event_date", format(taxEventDate, "yyyy-MM-dd"));
            } else {
              setData("tax_event_date", "");
            }
          }}
        />
        {errors.tax_event_date && (
          <p className="text-destructive text-sm">{errors.tax_event_date}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_date">Due date</Label>
        <DatePicker
          id="invoice_date"
          selected={data.due_date}
          onSelect={(dueDate) => {
            if (dueDate) {
              setData("due_date", format(dueDate, "yyyy-MM-dd"));
            } else {
              setData("due_date", "");
            }
          }}
        />
        {errors.due_date && (
          <p className="text-destructive text-sm">{errors.due_date}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_base_amount">Base amount</Label>
        <Input
          id="invoice_base_amount"
          type="number"
          step="0.01"
          value={data.base_amount}
          onChange={(e) => setData("base_amount", e.target.value)}
        />
        {errors.base_amount && (
          <p className="text-destructive text-sm">{errors.base_amount}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_vat_amount">VAT amount</Label>
        <Input
          id="invoice_vat_amount"
          type="number"
          step="0.01"
          value={data.vat_amount}
          onChange={(e) => setData("vat_amount", e.target.value)}
        />
        {errors.vat_amount && (
          <p className="text-destructive text-sm">{errors.vat_amount}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_total_amount">Total amount</Label>
        <Input
          id="invoice_total_amount"
          type="number"
          step="0.01"
          value={data.total_amount}
          onChange={(e) => setData("total_amount", e.target.value)}
        />
        {errors.total_amount && (
          <p className="text-destructive text-sm">{errors.total_amount}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_currency">Currency</Label>
        <Input
          id="invoice_currency"
          value={data.currency}
          onChange={(e) => {
            setData("currency", e.target.value.toUpperCase());
          }}
        />
        {errors.currency && (
          <p className="text-destructive text-sm">{errors.currency}</p>
        )}
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={processing}>
          Publish invoice
        </Button>
      </div>
    </form>
  );
}
